// Catalog.ID Client — the generic account gate
// ============================================================================
//
// Every request a module sends to this tab names the account it works for —
// `expectedCid`, the Catalog ID the desktop dashboard is signed in as (or, for
// an OIDC consent, the one the requester's own `oidc-identity` just learned).
// When this tab is signed in as somebody else the request is refused
// `account-mismatch` HERE, before any handler runs: no dialog, no unlock wait,
// no key-bag read, no write.
//
// Found live 2026-10-08: the dashboard said cfm5864fww, this tab deh3301ghb.
// The bitcash manager's "Sync with Catalog.ID" got a consent dialog releasing
// deh3301ghb's wallets toward a module working for cfm5864fww, and only then
// failed — safely, but cryptically — on its own AAD check.
//
// ONE place, every family, every mode, known or not: the dispatch in
// `client.ts` calls this before routing. Only two requests are exempt, both
// because they run before any dashboard account exists:
//   • `register-module` of the registry family — the module-identity handshake.
//     The helper answers it itself; it never reaches this tab at all.
//   • `oidc-identity` — how a sign-in LEARNS which account it will be.
// The key-grant family's `register-module` is NOT exempt: it writes a
// per-account module registration behind a dialog.
//
// The field rides inside the module-identity signature (a `|<cid>` suffix on
// the canonical bytes, `CidAccountScope` in ModuleIpc, verified by the helper's
// relay for the registry family and by `verify.ts` for the key-grant family),
// so a hop that rewrote or stripped it would fail verification rather than
// steer a request at another account.
// ============================================================================

/** The envelope field. Mirrors `CidAccountScope.WireField` (C#). */
export const EXPECTED_CID_FIELD = 'expectedCid';

/** Requests sent with no account, by design. Mirrors `CidAccountScope.IsAccountFree`. */
export function isAccountFree(type: string | undefined, mode: string | undefined): boolean {
    return type === 'registry-request' && (mode === 'register-module' || mode === 'oidc-identity');
}

export interface AccountGateEnvelope {
    type?: string;
    mode?: string;
    requestId?: string;
    requesterId?: string;
    expectedCid?: unknown;
}

export type AccountGateReason = 'account-missing' | 'account-mismatch' | 'no-active-identity';

export type AccountGateRefusal =
    | {
        type: 'registry-response'; to: string; requestId: string;
        status: 'unavailable'; reason: AccountGateReason; phase: 'before-persist';
    }
    | {
        type: 'key-grant-response'; to: string; requestId: string;
        status: 'unavailable'; reason: AccountGateReason;
    };

/**
 * The refusal to send instead of dispatching, or null to dispatch. `activeCid`
 * is the identity this tab is signed in as (null when nobody is).
 */
export function accountGateRefusal(
    env: AccountGateEnvelope,
    activeCid: string | null,
): AccountGateRefusal | null {
    if (isAccountFree(env.type, env.mode)) return null;

    const expected = typeof env.expectedCid === 'string' ? env.expectedCid : '';
    let reason: AccountGateReason | null = null;
    if (!expected) reason = 'account-missing';
    else if (!activeCid) reason = 'no-active-identity';
    else if (expected !== activeCid) reason = 'account-mismatch';
    if (!reason) return null;

    const to = env.requesterId ?? '';
    const requestId = env.requestId ?? '';
    return env.type === 'key-grant-request'
        ? { type: 'key-grant-response', to, requestId, status: 'unavailable', reason }
        : { type: 'registry-response', to, requestId, status: 'unavailable', reason, phase: 'before-persist' };
}
